import type { AuthResponse, ProblemDetails } from '@/shared/types/domain';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export class ApiError extends Error {
  readonly status: number;
  readonly errors: string[];
  readonly traceId?: string;

  constructor(problem: ProblemDetails) {
    super(problem.title);
    this.status = problem.status;
    this.errors = problem.errors ?? [];
    this.traceId = problem.traceId;
  }

  get userMessage(): string {
    return this.errors[0] ?? this.title;
  }

  get title(): string {
    return this.message;
  }
}

/**
 * Holds the access token in memory only (module-level variable) -- never
 * localStorage/sessionStorage/IndexedDB. This is a deliberate XSS mitigation:
 * a page reload always requires a fresh silent-refresh round-trip rather than
 * a token being readable from persistent storage by injected script.
 */
let accessToken: string | null = null;
let accessTokenExpiresAtUtc: string | null = null;

type AuthChangeListener = (auth: AuthResponse | null) => void;
const authChangeListeners = new Set<AuthChangeListener>();

export function setAccessToken(auth: AuthResponse | null): void {
  accessToken = auth?.accessToken ?? null;
  accessTokenExpiresAtUtc = auth?.accessTokenExpiresAtUtc ?? null;
  authChangeListeners.forEach((listener) => listener(auth));
}

export function onAuthChange(listener: AuthChangeListener): () => void {
  authChangeListeners.add(listener);
  return () => authChangeListeners.delete(listener);
}

function isAccessTokenExpiredOrExpiringSoon(): boolean {
  if (!accessTokenExpiresAtUtc) return true;
  const expiresAt = new Date(accessTokenExpiresAtUtc).getTime();
  // Refresh a little early (30s buffer) so a request never races the
  // token's actual expiry over a slow/flaky exhibition-hall connection.
  return Date.now() >= expiresAt - 30_000;
}

// Only one refresh call should ever be in flight at a time -- concurrent
// 401s from several parallel requests must not each trigger their own
// refresh (which would race and could invalidate each other's rotated token).
let refreshPromise: Promise<AuthResponse | null> | null = null;

async function refreshAccessToken(): Promise<AuthResponse | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include' // sends the httpOnly refreshToken cookie
      });

      if (!response.ok) {
        setAccessToken(null);
        return null;
      }

      const auth = (await response.json()) as AuthResponse;
      setAccessToken(auth);
      return auth;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skips the auth-refresh dance entirely -- used by login/refresh themselves. */
  skipAuth?: boolean;
}

async function parseErrorResponse(response: Response): Promise<ApiError> {
  try {
    const problem = (await response.json()) as ProblemDetails;
    return new ApiError(problem);
  } catch {
    return new ApiError({
      title: response.statusText || 'Request failed',
      status: response.status,
      errors: ['An unexpected error occurred.']
    });
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!options.skipAuth && isAccessTokenExpiredOrExpiringSoon() && accessTokenExpiresAtUtc !== null) {
    await refreshAccessToken();
  }

  const doFetch = () =>
    fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: options.signal,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(!options.skipAuth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      },
      body: options.body ? JSON.stringify(options.body) : undefined
    });

  let response = await doFetch();

  // One retry-after-refresh: covers the case where the token was still
  // technically unexpired by our clock but the server rejected it anyway
  // (clock skew, revocation, etc.).
  if (response.status === 401 && !options.skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await doFetch();
    }
  }

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const httpClient = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * For binary file responses (CSV/XLSX exports) rather than JSON -- shares
   * the same auth-header/silent-refresh logic as the JSON methods above,
   * then triggers a browser download instead of parsing the body as JSON.
   * The filename is read from the server's Content-Disposition header
   * (set in AnalyticsController) so the client doesn't need to duplicate
   * the server's naming convention.
   */
  async downloadFile(path: string): Promise<void> {
    if (isAccessTokenExpiredOrExpiringSoon() && accessTokenExpiresAtUtc !== null) {
      await refreshAccessToken();
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
      credentials: 'include',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    });

    if (!response.ok) {
      throw await parseErrorResponse(response);
    }

    const disposition = response.headers.get('Content-Disposition');
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? 'export';

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
